import { KeyboardEvent, MouseEvent, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { errorSnackbar } from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import { RootState } from "@src/reducers/store";
import { TicketModalInformationForRequestTechnicalSupportModalWithoutMachine } from "@src/reducers/your-reducer";
import { CustomerTicket } from "@src/types/support";
import { MachineNameAndSourceId } from "@common/types/server-list-types";
import { getAllSourcesListUseCase } from "@src/cloud-connect/client-configuration/use-cases/get-all-sources-list-use-case";
import { GetAllSourcesListApiResponse } from "@common/types/data-source-types";
import { useCreateSupportTicketStream } from "@src/cloud-connect/support/hooks/use-create-support-ticket-stream";
import { MAX_TICKET_TITLE_LENGTH } from "@src/cloud-connect/support/constants/constants";
import { useI18N } from "@src/language/language-context";
import { useSSEUnavailable } from "@src/cloud-connect/sse/use-sse-unavailable";

export const useAddRequestTechnicalSupportDrawerModalWithoutMachine = () => {
  const i18n = useI18N();

  const [issueTitle, setIssueTitle] = useState<string>("");
  const [issueDetails, setIssueDetails] = useState<string>("");
  const [images, setImages] = useState<File[]>([]);
  const [selectedMachine, setSelectedMachine] =
    useState<MachineNameAndSourceId | null>(null);
  const [isDetailsExpanded, setIsDetailsExpanded] = useState<boolean>(true);
  const [machineOptions, setMachineOptions] = useState<
    MachineNameAndSourceId[]
  >([]);
  const [loadMachineListLoading, setLoadMachineListLoading] = useState(true);

  const { submitSupportTicket, isSubmitLoading } =
    useCreateSupportTicketStream();

  const ticketModalInformation: TicketModalInformationForRequestTechnicalSupportModalWithoutMachine =
    useSelector(
      (state: RootState) =>
        state.yourSlice.modals
          .addRequestTechnicalSupportDrawerModalWithoutMachine
          .ticketModalInformation,
    );

  const userId = useSelector(
    (state: RootState) => state.yourSlice.authContext.userId,
  );

  const isIssueTitleTooLong = issueTitle.length > MAX_TICKET_TITLE_LENGTH;
  const isSSEUnavailable = useSSEUnavailable("service-support");
  const isSubmitDisabled: boolean =
    !issueTitle.trim() ||
    !issueDetails.trim() ||
    isIssueTitleTooLong ||
    isSSEUnavailable;

  const handleToggleDetails = (): void => setIsDetailsExpanded((prev) => !prev);

  const onChangeIssueTitle = (value: string): void => {
    setIssueTitle(value);
  };

  const onChangeIssueDetails = (value: string): void => {
    setIssueDetails(value);
  };

  const onSelectMachine = (value: MachineNameAndSourceId | null): void => {
    setSelectedMachine(value);
  };

  const addImages = (files: File[]): void => setImages(files);

  const removeImage = (index: number): void =>
    setImages((prev) => prev.filter((_, i) => i !== index));

  const buildStructuredMachineListData = (
    dataSources: GetAllSourcesListApiResponse["data_sources"],
  ): MachineNameAndSourceId[] => {
    return Object.values(dataSources).map((source) => ({
      name: source.name,
      source_id: source.source_id,
    }));
  };

  const loadData = async () => {
    setLoadMachineListLoading(true);
    const result = await getAllSourcesListUseCase();

    if (!result.success || !result.data) {
      setMachineOptions([]);
      errorSnackbar(result.error);
      return;
    }
    const data = buildStructuredMachineListData(result.data);
    setMachineOptions(data);
    setLoadMachineListLoading(false);
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleSubmitRequestTechnicalSupport = async (
    e: MouseEvent | KeyboardEvent,
  ): Promise<void> => {
    e.preventDefault();

    if (isSSEUnavailable) return;

    if (isIssueTitleTooLong) {
      errorSnackbar(
        i18n(
          "modules.addRequestTechnicalSupportDrawerModal.issueTitleTooLongError",
        ),
      );
      return;
    }

    const payload: CustomerTicket = {
      type: "CUSTOMER_TICKET",
      raised_by: userId,
      priority: null,
      status: "new",
      title: issueTitle,
      description: issueDetails,
    };

    await submitSupportTicket({
      payload,
      files: images,
      contextQueryDatasource: selectedMachine?.source_id || null,
      contextQueryEvents: ticketModalInformation.notification_information
        ? [ticketModalInformation?.notification_information?.event_id]
        : null,
      closeDrawerModalId: "addRequestTechnicalSupportDrawerModalWithoutMachine",
      onTicketCreated: () => {
        setIssueTitle("");
        setIssueDetails("");
        setSelectedMachine(null);
        setImages([]);
      },
    });
  };

  return {
    issueTitle,
    issueDetails,
    onChangeIssueTitle,
    onChangeIssueDetails,
    loadMachineListLoading,
    selectedMachine,
    machineOptions,
    onSelectMachine,
    handleSubmitRequestTechnicalSupport,
    isSubmitDisabled,
    isSubmitRequestTechnicalSupportWithoutMachineLoading: isSubmitLoading,
    isSSEUnavailable,
    isDetailsExpanded,
    handleToggleDetails,
    images,
    addImages,
    removeImage,
  };
};
